/** Google Chat credential validation shared by the Setup Server and the Control Plane (google-chat-integration.md §3). */
import { createPrivateKey } from 'node:crypto';
import { SignJWT } from 'jose';
/** Google's fixed OAuth token endpoint; the key's own `token_uri` is never followed. */
export const GOOGLE_TOKEN_ENDPOINT = 'https://oauth2.googleapis.com/token';
/** The Chat REST root; the probe and the claim's reads below are the only Chat calls this module makes. */
export const GOOGLE_CHAT_API_ROOT = 'https://chat.googleapis.com/v1';
/** The one bounded read that proves the credential, and shows which Workspace customers own the named Spaces the app is in (§10.3); nothing is ever posted. */
export const GOOGLE_CHAT_PROBE_URL = `${GOOGLE_CHAT_API_ROOT}/spaces?pageSize=100&filter=${encodeURIComponent('spaceType = "SPACE"')}`;
/** App authentication only: no user impersonation and no domain-wide delegation (§3). */
export const GOOGLE_CHAT_BOT_SCOPE = 'https://www.googleapis.com/auth/chat.bot';
/** Read-only scope for resolving the key's own project number; a `chat.bot` token cannot read it. */
export const GOOGLE_CLOUD_READ_ONLY_SCOPE = 'https://www.googleapis.com/auth/cloud-platform.read-only';
/** Cloud Resource Manager's fixed project read, which reports the project's number beside its ID. */
export function googleCloudProjectUrl(projectId) {
    return `https://cloudresourcemanager.googleapis.com/v1/projects/${encodeURIComponent(projectId)}`;
}
/** Give up on a silent endpoint rather than hold the save request open. */
const PROBE_TIMEOUT_MS = 5_000;
/** Bound provider text echoed into an operator-facing message. */
const MAX_PROVIDER_DETAIL = 300;
/** Connectivity failures are retryable; every other failure is the credential's or the project's. */
export function probeFailureIsConnectivity(status) {
    return status === 'unreachable' || status === 'google_unavailable';
}
/** The numeric Project number from the Google Cloud dashboard; it names the add-on service account that signs the app's requests (§11.2). */
export function isGoogleCloudProjectNumber(value) {
    return /^[1-9]\d{0,19}$/.test(value);
}
/** A user-managed service account's email; its domain names the project that owns the account (§3). */
const USER_MANAGED_SERVICE_ACCOUNT = /^[a-z][a-z0-9-]{4,28}[a-z0-9]@([a-z][a-z0-9-]{4,28}[a-z0-9])\.iam\.gserviceaccount\.com$/;
/** The project that owns a user-managed service account, read from its email; undefined for any other account. */
export function serviceAccountProject(clientEmail) {
    return USER_MANAGED_SERVICE_ACCOUNT.exec(clientEmail)?.[1];
}
/** Accept only a key of a user-managed service account owned by the declared project; the token exchange authenticates its email, not `project_id` (§3). */
export function checkServiceAccountKey(raw, projectId) {
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return {
            status: 'invalid_key',
            message: 'the service-account key is not valid JSON; paste the whole downloaded key file'
        };
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return { status: 'invalid_key', message: 'the service-account key must be a JSON object' };
    }
    const fields = parsed;
    if (fields.type !== 'service_account') {
        return {
            status: 'invalid_key',
            message: 'only a service-account JSON key is accepted; create one under IAM & Admin → Service Accounts → Keys'
        };
    }
    const text = (name) => {
        const value = fields[name];
        return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
    };
    const keyProjectId = text('project_id');
    const clientEmail = text('client_email');
    const privateKey = text('private_key');
    if (!keyProjectId || !clientEmail || !privateKey) {
        return {
            status: 'invalid_key',
            message: 'the service-account key is missing project_id, client_email, or private_key'
        };
    }
    const owningProject = serviceAccountProject(clientEmail);
    if (!owningProject) {
        return {
            status: 'invalid_key',
            message: `the Chat app needs a service account created in its own project (name@project-id.iam.gserviceaccount.com); ${clientEmail} is not one`
        };
    }
    if (keyProjectId !== owningProject) {
        return {
            status: 'invalid_key',
            message: `the key's project_id ${keyProjectId} does not match its service account's project ${owningProject}`
        };
    }
    if (owningProject !== projectId) {
        return {
            status: 'project_mismatch',
            message: `the key belongs to project ${owningProject}, not ${projectId}; keep the Chat app and its service account in one project`
        };
    }
    let signingKey;
    try {
        signingKey = createPrivateKey({ key: privateKey, format: 'pem' });
    }
    catch {
        return { status: 'invalid_key', message: 'the private_key in the service-account key could not be read' };
    }
    if (signingKey.asymmetricKeyType !== 'rsa') {
        return { status: 'invalid_key', message: 'the private_key in the service-account key is not an RSA key' };
    }
    const privateKeyId = text('private_key_id');
    return {
        status: 'ok',
        key: {
            projectId: owningProject,
            clientEmail,
            ...(privateKeyId ? { privateKeyId } : {}),
            signingKey,
            json: JSON.stringify(parsed)
        }
    };
}
/** Mint an app token for `chat.bot` and list at most one space; never sends a message (§3). */
export async function probeGoogleChatCredential(key, fetchImpl, now = () => new Date()) {
    const minted = await mintAccessToken(key, GOOGLE_CHAT_BOT_SCOPE, fetchImpl, now);
    if ('status' in minted)
        return minted;
    let chatResponse;
    try {
        chatResponse = await fetchImpl(GOOGLE_CHAT_PROBE_URL, {
            method: 'GET',
            headers: { authorization: `Bearer ${minted.accessToken}`, accept: 'application/json' },
            signal: AbortSignal.timeout(PROBE_TIMEOUT_MS)
        });
    }
    catch {
        return unreachable('the Google Chat API');
    }
    if (chatResponse.ok) {
        const customerId = soleCustomerOf(await readJson(chatResponse));
        return {
            status: 'ok',
            message: `${key.clientEmail} authenticated to the Google Chat API as the project's Chat app`,
            ...(customerId ? { customerId } : {})
        };
    }
    if (chatResponse.status >= 500 || chatResponse.status === 429) {
        return unavailable('the Google Chat API', chatResponse.status);
    }
    const body = await readJson(chatResponse);
    const reason = detail(body?.error?.message) ?? 'no details';
    if (chatResponse.status === 401) {
        return {
            status: 'key_rejected',
            message: `Authentication failed: the Google Chat API did not accept the token issued to ${key.clientEmail} (${reason}). Create a new JSON key for that service account and paste it here.`
        };
    }
    return {
        status: 'chat_api_refused',
        message: chatResponse.status === 404
            ? `Chat app not found: project ${key.projectId} has no configured Chat app (${reason}). Configure the Chat app in Google Cloud Console first, then save again.`
            : `Chat API refused the app: HTTP ${chatResponse.status} for ${key.clientEmail} (${reason}). Enable the Google Chat API in project ${key.projectId} and configure its Chat app.`
    };
}
/** Read the key's own project from Cloud Resource Manager, so its number is authenticated rather than entered (§3). */
export async function resolveProjectNumber(key, fetchImpl, now = () => new Date()) {
    const minted = await mintAccessToken(key, GOOGLE_CLOUD_READ_ONLY_SCOPE, fetchImpl, now);
    if ('status' in minted)
        return minted;
    let response;
    try {
        response = await fetchImpl(googleCloudProjectUrl(key.projectId), {
            method: 'GET',
            headers: { authorization: `Bearer ${minted.accessToken}`, accept: 'application/json' },
            signal: AbortSignal.timeout(PROBE_TIMEOUT_MS)
        });
    }
    catch {
        return unreachable('the Cloud Resource Manager API');
    }
    if (response.status >= 500 || response.status === 429) {
        return unavailable('the Cloud Resource Manager API', response.status);
    }
    const body = await readJson(response);
    if (response.ok) {
        const projectNumber = typeof body?.projectNumber === 'string' ? body.projectNumber : undefined;
        if (projectNumber && isGoogleCloudProjectNumber(projectNumber) && body?.projectId === key.projectId) {
            return { status: 'ok', projectNumber };
        }
        return {
            status: 'project_unresolved',
            message: `Cloud Resource Manager did not return the number of project ${key.projectId}; check the project ID and try again.`
        };
    }
    const error = body?.error;
    const reason = detail(error?.message) ?? 'no details';
    if (response.status === 401) {
        return {
            status: 'key_rejected',
            message: `Authentication failed: the Cloud Resource Manager API did not accept the token issued to ${key.clientEmail} (${reason}). Create a new JSON key for that service account and paste it here.`
        };
    }
    if (response.status === 403) {
        const details = Array.isArray(error?.details) ? error.details : [];
        const serviceDisabled = details.some((entry) => typeof entry === 'object' && entry !== null && entry.reason === 'SERVICE_DISABLED');
        return serviceDisabled
            ? {
                status: 'crm_disabled',
                message: `Enable the Cloud Resource Manager API in project ${key.projectId}: AgentConnect reads the project's number through it to bind the Chat app to this key.`
            }
            : {
                status: 'crm_forbidden',
                message: `Grant ${key.clientEmail} the Browser role on project ${key.projectId} (resourcemanager.projects.get): AgentConnect reads the project's number with it to bind the Chat app to this key.`
            };
    }
    return {
        status: 'project_unresolved',
        message: `Cloud Resource Manager refused to read project ${key.projectId}: HTTP ${response.status} (${reason}). Check the project ID and try again.`
    };
}
/** Every check a Chat app passes before it is stored: key shape, the authenticated project number, then the `chat.bot` read (§3). */
export async function checkGoogleChatApp(input, fetchImpl, now = () => new Date()) {
    if (input.projectNumber !== undefined && !isGoogleCloudProjectNumber(input.projectNumber)) {
        return {
            status: 'project_number_invalid',
            message: 'the project number must be the numeric Project number from the Google Cloud dashboard'
        };
    }
    const checked = checkServiceAccountKey(input.serviceAccountKey, input.projectId);
    if (checked.status !== 'ok')
        return checked;
    const resolved = await resolveProjectNumber(checked.key, fetchImpl, now);
    if (resolved.status !== 'ok')
        return resolved;
    if (input.projectNumber !== undefined && input.projectNumber !== resolved.projectNumber) {
        return {
            status: 'project_number_mismatch',
            message: `the project number ${input.projectNumber} does not match project ${input.projectId}, whose number is ${resolved.projectNumber}`
        };
    }
    const probe = await probeGoogleChatCredential(checked.key, fetchImpl, now);
    if (probe.status !== 'ok')
        return { status: probe.status, message: probe.message };
    return {
        status: 'ok',
        key: checked.key,
        projectNumber: resolved.projectNumber,
        message: probe.message,
        ...(probe.customerId ? { customerId: probe.customerId } : {})
    };
}
/** The bare id of the one `customers/…` every listed named Space carries; undefined for none, several, or a list with more pages. */
function soleCustomerOf(body) {
    if (!body || body.nextPageToken)
        return undefined;
    const spaces = Array.isArray(body.spaces) ? body.spaces : [];
    const customers = new Set();
    for (const space of spaces) {
        const entry = typeof space === 'object' && space !== null ? space : undefined;
        if (entry?.spaceType !== 'SPACE')
            continue;
        const customer = typeof entry.customer === 'string' ? /^customers\/([A-Za-z0-9_-]{1,128})$/.exec(entry.customer)?.[1] : undefined;
        if (!customer)
            return undefined;
        customers.add(customer);
    }
    return customers.size === 1 ? [...customers][0] : undefined;
}
/** App-authenticated Chat API reads under one lazily minted `chat.bot` token; nothing is ever written. */
export function googleChatAppReader(key, fetchImpl, now = () => new Date()) {
    let token;
    return async (path) => {
        token ??= mintAccessToken(key, GOOGLE_CHAT_BOT_SCOPE, fetchImpl, now);
        const minted = await token;
        if ('status' in minted)
            return minted;
        let response;
        try {
            response = await fetchImpl(`${GOOGLE_CHAT_API_ROOT}/${path}`, {
                method: 'GET',
                headers: { authorization: `Bearer ${minted.accessToken}`, accept: 'application/json' },
                signal: AbortSignal.timeout(PROBE_TIMEOUT_MS)
            });
        }
        catch {
            return unreachable('the Google Chat API');
        }
        if (response.status >= 500 || response.status === 429)
            return unavailable('the Google Chat API', response.status);
        const body = await readJson(response);
        if (response.ok && body)
            return { status: 'ok', body };
        const reason = detail(body?.error?.message) ?? `HTTP ${response.status}`;
        if (response.status === 401)
            return { status: 'key_rejected', message: `the Google Chat API rejected the app token (${reason})` };
        if (response.status === 404)
            return { status: 'not_found', message: `the Google Chat API found no ${path} (${reason})` };
        return { status: 'refused', message: `the Google Chat API refused to read ${path} (${reason})` };
    };
}
/** One app-authenticated access token for `scope`, from Google's fixed token endpoint. */
async function mintAccessToken(key, scope, fetchImpl, now) {
    const issuedAt = Math.floor(now().getTime() / 1000);
    const assertion = await new SignJWT({ scope })
        .setProtectedHeader({ alg: 'RS256', typ: 'JWT', ...(key.privateKeyId ? { kid: key.privateKeyId } : {}) })
        .setIssuer(key.clientEmail)
        .setAudience(GOOGLE_TOKEN_ENDPOINT)
        .setIssuedAt(issuedAt)
        .setExpirationTime(issuedAt + 3_600)
        .sign(key.signingKey);
    let tokenResponse;
    try {
        tokenResponse = await fetchImpl(GOOGLE_TOKEN_ENDPOINT, {
            method: 'POST',
            headers: { 'content-type': 'application/x-www-form-urlencoded', accept: 'application/json' },
            body: new URLSearchParams({ grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer', assertion }).toString(),
            signal: AbortSignal.timeout(PROBE_TIMEOUT_MS)
        });
    }
    catch {
        return unreachable("Google's token endpoint");
    }
    const token = await readJson(tokenResponse);
    if (tokenResponse.status >= 500 || tokenResponse.status === 429) {
        return unavailable("Google's token endpoint", tokenResponse.status);
    }
    if (!tokenResponse.ok) {
        const error = detail(token?.error) ?? `HTTP ${tokenResponse.status}`;
        const description = detail(token?.error_description);
        return {
            status: 'key_rejected',
            message: `Authentication failed: Google rejected the service-account key for ${key.clientEmail} (${error}${description ? `: ${description}` : ''}). ` +
                'The key may have been deleted or disabled, or its service account removed; create a new JSON key for that service account and paste it here.'
        };
    }
    const accessToken = typeof token?.access_token === 'string' ? token.access_token : undefined;
    if (!accessToken)
        return unavailable("Google's token endpoint", tokenResponse.status);
    return { accessToken };
}
function unreachable(target) {
    return {
        status: 'unreachable',
        message: `Connection failed: ${target} could not be reached. Check outbound HTTPS access to Google and try again; nothing was saved.`
    };
}
function unavailable(target, status) {
    return {
        status: 'google_unavailable',
        message: `Connection failed: ${target} answered HTTP ${status} without a usable result. Try again later; nothing was saved.`
    };
}
async function readJson(response) {
    try {
        const body = await response.json();
        return typeof body === 'object' && body !== null && !Array.isArray(body)
            ? body
            : undefined;
    }
    catch {
        return undefined;
    }
}
function detail(value) {
    if (typeof value !== 'string' || value.trim() === '')
        return undefined;
    const trimmed = value.trim();
    return trimmed.length > MAX_PROVIDER_DETAIL ? `${trimmed.slice(0, MAX_PROVIDER_DETAIL)}…` : trimmed;
}
//# sourceMappingURL=credential.js.map